import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import ReactSelect, { components } from 'react-select';
import { TextSpan, fontSizes } from 'app/typography';
import { dropdownColors } from 'app/colors';
import { displayBreakpoints, isMobile, useWindowSize } from 'app/spacing';
import Icon from 'components/Kizen/Icon';
import { StyledLabel, StyledLabelWrapper, reactSelectStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { StyledCustomOption } from '../Select/styles';
import { EMPTY_OBJECT } from 'utility/fieldHelpers';

// The way the input and selected value are overlaid,
// we have a flex layout that sizes the input to the selected value.
// The input is absolutely positioned so that they can overlap.
// When the user starts typing react-select removes the element
// selected value element, which means the flex box loses its sizing.
// In order to counteract this, once the user starts typing, we measure
// the size of the box and switch the input to a relatively positioned
// element of a fixed size.

const FrozenWidthInput = function FrozenWidthInput({
  value,
  onChange,
  ...others
}) {
  const [width, setWidth] = useState(null);
  const handleChange = useCallback(
    (ev) => {
      if (!value && ev.target.value) {
        // We use getBoundingClientRect() rather than offsetWidth
        // because if we don't capture fractional sizing, there may be
        // jitter when the user starts typing.
        const rect = ev.target.parentElement.getBoundingClientRect();
        setWidth(rect.width);
      }
      onChange(ev);
    },
    [onChange, value]
  );
  return (
    <components.Input
      style={value ? { width, position: 'relative', top: 0 } : null}
      value={value}
      onChange={handleChange}
      {...others}
    />
  );
};

const DropdownIndicatorIcon = styled(Icon)`
  && {
    width: 12px;

    svg {
      width: 12px;
      height: auto;
    }
  }
`;

const getValue = (value, options) => {
  // if the value is a string, find that in the options and return it
  if (typeof value === 'string') {
    return options.find((option) => option.value === value);
  }

  // if the value is an object, key off of the id
  if (value && 'id' in value) {
    return options.find((option) => option.value.id === value.id);
  }

  return value;
};

const MainSelector = ({
  className = '',
  label = '',
  textSize = 'text',
  size = 'small',
  style,
  styles = EMPTY_OBJECT,
  disabled = false,
  error = false,
  options,
  value = null,
  components: selectComponents = EMPTY_OBJECT,
  kdsCompatability,
  menuPortalTarget,
  placeholder = '',
  ...props
}) => {
  const { width } = useWindowSize();
  const { t } = useTranslation();
  const NoOptionsMessage = ({ children, ...propsNoOption }) => {
    return (
      <components.NoOptionsMessage {...propsNoOption}>
        <TextSpan
          size={textSize}
          style={{ color: 'var(--color-font-placeholder)' }}
        >
          {t('No Options')}
        </TextSpan>
      </components.NoOptionsMessage>
    );
  };

  const Placeholder = ({ children, ...propsPlaceholder }) => {
    return (
      <components.Placeholder {...propsPlaceholder}>
        <TextSpan size={textSize} color="var(--color-font-placeholder)">
          {children}
        </TextSpan>
      </components.Placeholder>
    );
  };

  const SingleValue = ({ children, ...propsSingleValue }) => {
    return (
      <components.SingleValue {...propsSingleValue}>
        <TextSpan
          size={textSize}
          style={{
            textIndent: '2px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            margin: 0,
            fontSize: fontSizes.subheader,
            fontWeight: '400',
            marginLeft: 0,
          }}
        >
          {children}
        </TextSpan>
      </components.SingleValue>
    );
  };

  const DropdownIndicator = (props) => {
    const hideArrow = props.selectProps.readOnly ?? false;

    if (hideArrow) {
      return null;
    }

    return (
      <DropdownIndicatorIcon
        icon="down"
        color="var(--color-button-secondary-default)"
      />
    );
  };

  if (label) {
    return (
      <div className={className}>
        <StyledLabelWrapper>
          <StyledLabel disabled={disabled}>{label}</StyledLabel>
          <ReactSelect
            styles={{ ...reactSelectStyles, ...styles }}
            components={{
              SingleValue,
              Placeholder,
              NoOptionsMessage,
              DropdownIndicator,
              Input: FrozenWidthInput,
              Option: StyledCustomOption,
              ...selectComponents,
            }}
            options={options}
            // react-select anticipates a value of the shape { value, label },
            // i.e. one of the values in options.
            value={getValue(value, options)}
            mobile={isMobile(width, displayBreakpoints.desktop)}
            {...props}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: dropdownColors.hover,
              },
              controlHeight: 36,
            })}
            isDisabled={disabled}
            error={error}
            placeholder={placeholder}
          />
        </StyledLabelWrapper>
      </div>
    );
  }

  return (
    <div className={className}>
      <ReactSelect
        styles={{ ...reactSelectStyles, ...styles }}
        components={{
          SingleValue,
          Placeholder,
          NoOptionsMessage,
          DropdownIndicator,
          Input: FrozenWidthInput,
          Option: StyledCustomOption,
          ...selectComponents,
        }}
        options={options}
        // react-select anticipates a value of the shape { value, label },
        // i.e. one of the values in options.
        value={getValue(value, options)}
        mobile={isMobile(width, displayBreakpoints.desktop)}
        {...props}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: dropdownColors.hover,
          },
        })}
        isDisabled={disabled}
        error={error}
        menuPortalTarget={kdsCompatability ? document.body : menuPortalTarget}
        placeholder={placeholder}
      />
    </div>
  );
};

export default MainSelector;
